import SettingsContainer from 'trunks/vue/admin/components/settings/settings_container.js'
import CodeEditor from './code_editor.js'

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

function cloneTemplate(template) {
  return {
    type: 'Template::Category',
    name: '',
    enable_v2_layout: false,
    raw_body: '',
    scss: '',
    javascript: '',
    raw_body_for_pc: '',
    scss_for_pc: '',
    javascript_for_pc: '',
    ...(template || {})
  }
}

export default Vue.extend({
  components: {
    'settings-container': SettingsContainer,
    'code-editor': CodeEditor
  },

  props: {
    mode: {
      type: String,
      required: true
    },
    formData: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      template: cloneTemplate(this.formData.template),
      templateFor: this.formData.template_for || 'mobile',
      saving: false
    }
  },

  computed: {
    isNew() {
      return this.mode === 'new'
    },

    isPc() {
      return this.templateFor === 'pc'
    },

    title() {
      if (this.isNew) return '自定义新的模板'
      return `修改${this.template.human_type}模板`
    },

    paths() {
      return this.formData.paths || {}
    },

    templateTypes() {
      return this.formData.template_types || []
    },

    authenticityToken() {
      return csrfToken()
    },

    formAction() {
      return this.isNew ? this.paths.create : this.paths.update
    }
  },

  methods: {
    switchTemplateFor(value) {
      this.templateFor = value
    },

    onSubmit(event) {
      if (this.isNew) return
      event.preventDefault()
      this.saveTemplate()
    },

    saveTemplate() {
      this.saving = true
      $.ajax({
        url: this.paths.update,
        type: 'patch',
        dataType: 'json',
        data: {
          template: this.template
        },
        success: (payload) => {
          if (payload.template) this.template = cloneTemplate(payload.template)
          Skylark.Bridge.newAlertify(payload.message || '模板更新成功', { type: 'success' })
        },
        error: (xhr) => {
          let msg = '模板更新失败'
          try {
            msg = JSON.parse(xhr.responseText).message || msg
          } catch (e) {
            // ignore
          }
          Skylark.Bridge.newAlertify(msg, { type: 'danger' })
        },
        complete: () => {
          this.saving = false
        }
      })
    }
  },

  template: `
    <settings-container active-tab="templates">
      <template slot="content">
        <div class="templates-settings template-form-page">
          <div class="template-form-header">
            <div>
              <div class="templates-kicker">模板设置</div>
              <h1>{{ title }}</h1>
              <p>{{ isNew ? '创建自定义页面模板，保存后可在模板列表继续编辑。' : '编辑移动端和电脑版模板内容。' }}</p>
            </div>
            <a :href="paths.index" class="btn btn-default btn-small outlined">返回模板列表</a>
          </div>

          <div class="template-form-card">
            <form :action="formAction" method="post" class="template-form" @submit="onSubmit">
              <input type="hidden" name="authenticity_token" :value="authenticityToken" />
              <template v-if="isPc">
                <input type="hidden" name="template[enable_v2_layout]" :value="template.enable_v2_layout" />
                <input type="hidden" name="template[raw_body]" :value="template.raw_body" />
                <input type="hidden" name="template[scss]" :value="template.scss" />
                <input type="hidden" name="template[javascript]" :value="template.javascript" />
              </template>
              <template v-else>
                <input type="hidden" name="template[raw_body_for_pc]" :value="template.raw_body_for_pc" />
                <input type="hidden" name="template[scss_for_pc]" :value="template.scss_for_pc" />
                <input type="hidden" name="template[javascript_for_pc]" :value="template.javascript_for_pc" />
              </template>

              <div class="template-form-toolbar">
                <div class="template-device-tabs tabs">
                  <div class="tab-navs reverse nav">
                    <button type="button"
                            :class="['nav-item', { active: !isPc }]"
                            @click="switchTemplateFor('mobile')">
                      手机版
                    </button>
                    <button type="button"
                            :class="['nav-item', { active: isPc }]"
                            @click="switchTemplateFor('pc')">
                      电脑版
                    </button>
                  </div>
                </div>

                <label v-if="!isPc" class="template-layout-toggle">
                  <input type="hidden" name="template[enable_v2_layout]" value="false" />
                  <input type="checkbox"
                         name="template[enable_v2_layout]"
                         value="true"
                         v-model="template.enable_v2_layout" />
                  <span>启用 v2 布局</span>
                </label>
              </div>

              <div class="template-meta-fields">
                <div v-if="isNew" class="form-row">
                  <label>模板类型</label>
                  <select class="controlled-input" name="template[type]" v-model="template.type">
                    <option v-for="option in templateTypes" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div v-if="isNew || !template.default" class="form-row">
                  <label>名称</label>
                  <input class="controlled-input" name="template[name]" v-model="template.name" required />
                </div>
                <div v-else class="form-row">
                  <label>名称</label>
                  <div class="template-readonly-value">{{ template.name }}</div>
                </div>
              </div>

              <div class="template-editor-grid">
                <template v-if="!isPc">
                  <code-editor label="HTML"
                               name="template[raw_body]"
                               mode="text/html"
                               v-model="template.raw_body" />
                  <code-editor label="SCSS"
                               name="template[scss]"
                               mode="text/css"
                               v-model="template.scss" />
                  <code-editor label="JavaScript"
                               name="template[javascript]"
                               mode="text/javascript"
                               v-model="template.javascript" />
                </template>

                <template v-else>
                  <code-editor label="HTML"
                               name="template[raw_body_for_pc]"
                               mode="text/html"
                               v-model="template.raw_body_for_pc" />
                  <code-editor label="SCSS"
                               name="template[scss_for_pc]"
                               mode="text/css"
                               v-model="template.scss_for_pc" />
                  <code-editor label="JavaScript"
                               name="template[javascript_for_pc]"
                               mode="text/javascript"
                               v-model="template.javascript_for_pc" />
                </template>
              </div>

              <div class="template-form-actions">
                <a :href="paths.index" class="btn btn-default outlined">取消</a>
                <button type="submit" class="btn btn-success" :disabled="saving">
                  {{ isNew ? '新建' : (saving ? '保存中...' : '更新') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </template>
    </settings-container>
  `
})
